import React, { useState, useEffect } from 'react';
import { getWhatsAppConfig, saveWhatsAppConfig, isWhatsAppConfigured } from '../services/whatsappService';
import { getAIConfig, saveAIConfig, isAIConfigured } from '../services/aiDispatchService';
import { getGrokConfig, saveGrokConfig, isGrokConfigured, parseMessageWithGrok, createDraftLoadFromMessage } from '../services/grokAIService';
import { getEmailConfig, saveEmailConfig, isEmailConfigured, EMAIL_PROVIDERS, getLastEmailSync } from '../services/emailService';
import { getIncomingMessages } from '../services/whatsappWebhookService';
import { getLocalRoutingConfig, saveRoutingConfig, isGoogleMapsConfigured } from '../services/routingEngineService';
import { loadSharedMapsKey, saveSharedMapsKey, looksLikeBrowserKey, ensureGoogleMaps, mapsKeyProblemText, getCachedSharedMapsKey } from '../services/mapsKeyService';
import { UNIT_SYSTEMS, getDefaultUnitSystem, setDefaultUnitSystem, formatWeightBoth, formatLengthBoth } from '../utils/units';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import DwellTimeSettings from './DwellTimeSettings';
import EquipmentRulesSettings from './EquipmentRulesSettings';   // __EQUIP_COMPAT__
import DayGraphSettings from './DayGraphSettings';   // __DAY_GRAPH__
import { saveSettingToDatabase, SETTINGS_KEYS } from '../services/settingsPersistenceService';

function SettingsModal({ onClose }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('preferences');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Unit preferences
  const [unitSystem, setUnitSystem] = useState(getDefaultUnitSystem());
  const [gpsInterval, setGpsInterval] = useState(30);
  
  // WhatsApp settings
  const [whatsappConfig, setWhatsappConfig] = useState({
    accountSid: '',
    authToken: '',
    fromNumber: ''
  });
  
  // AI settings (OpenAI for dispatch)
  const [aiConfig, setAiConfig] = useState({
    apiKey: '',
    model: 'gpt-3.5-turbo'
  });

  // Grok-3 settings (xAI for message parsing)
  const [grokConfig, setGrokConfig] = useState({
    apiKey: '',
    model: 'grok-3'
  });

  // Email integration settings
  const [emailConfig, setEmailConfig] = useState({
    provider: 'YAHOO',
    email: '',
    password: '',
    imapHost: '',
    imapPort: 993,
    enabled: false,
    pollingInterval: 5
  });

  // Google Maps settings
  const [googleMapsConfig, setGoogleMapsConfig] = useState({
    googleMapsApiKey: '',
    trafficModel: 'best_guess',
    avoidTolls: false,
    avoidHighways: false,
    optimizeWaypoints: true,
    defaultDwellMinutes: 30,
    region: '',
    homeLocation: '',
    homeLat: null,
    homeLng: null,
    homeRadius: 50,
    dayStartTime: '07:00',
    startFromYard: true
  });

  // __BOARD_MAPS_KEY__ the shared browser key (every office computer), separate
  // from this login's own key below (which is now only the fallback).
  const [sharedMapsKey, setSharedMapsKey] = useState('');
  const [sharedMapsKeySaved, setSharedMapsKeySaved] = useState('');
  const [savingSharedKey, setSavingSharedKey] = useState(false);

  // Manual message input for Grok parsing
  const [manualMessage, setManualMessage] = useState('');
  const [parsedResult, setParsedResult] = useState(null);
  const [parsing, setParsing] = useState(false);

  // Integration status
  const [lastEmailSync, setLastEmailSyncState] = useState(null);
  const [whatsappMessageCount, setWhatsappMessageCount] = useState(0);

    useEffect(() => {
      // Load existing configs
      const existingWhatsApp = getWhatsAppConfig();
      if (existingWhatsApp) {
        setWhatsappConfig(existingWhatsApp);
      }
    
      const existingAI = getAIConfig();
      if (existingAI) {
        setAiConfig(existingAI);
      }

      const existingGrok = getGrokConfig();
      if (existingGrok) {
        setGrokConfig(existingGrok);
      }

      const existingEmail = getEmailConfig();
      if (existingEmail) {
        setEmailConfig(existingEmail);
      }

      // __BOARD_MAPS_KEY__ the form edits THIS login's config (not the merged
      // one), so the shared key is never copied into a personal profile.
      const existingGoogleMaps = getLocalRoutingConfig();
      if (existingGoogleMaps) {
        setGoogleMapsConfig(existingGoogleMaps);
      }
      setSharedMapsKey(getCachedSharedMapsKey());
      setSharedMapsKeySaved(getCachedSharedMapsKey());
      loadSharedMapsKey(true).then((k) => { setSharedMapsKey(k || ''); setSharedMapsKeySaved(k || ''); }).catch(() => {});

      // Load integration status
      setLastEmailSyncState(getLastEmailSync());
      setWhatsappMessageCount(getIncomingMessages().length);

      // Load all settings from profile database
      const loadProfileSettings = async () => {
        if (user?.id) {
          const { data } = await supabase
            .from('profiles')
            .select('settings')
            .eq('id', user.id)
            .single();
          if (data?.settings) {
            if (data.settings.unitSystem) {
              setUnitSystem(data.settings.unitSystem);
              setDefaultUnitSystem(data.settings.unitSystem);
            }
            if (data.settings.routingConfig) {
              setGoogleMapsConfig(prev => ({ ...prev, ...data.settings.routingConfig }));
            }
            if (data.settings.whatsappConfig) {
              setWhatsappConfig(prev => ({ ...prev, ...data.settings.whatsappConfig }));
            }
            if (data.settings.aiDispatchConfig) {
              setAiConfig(prev => ({ ...prev, ...data.settings.aiDispatchConfig }));
            }
            if (data.settings.grokConfig) {
              setGrokConfig(prev => ({ ...prev, ...data.settings.grokConfig }));
            }
            if (data.settings.emailConfig) {
              setEmailConfig(prev => ({ ...prev, ...data.settings.emailConfig }));
            }
            if (data.settings.gpsIntervalSeconds) {
              setGpsInterval(data.settings.gpsIntervalSeconds);
            }
          }
        }
      };
      loadProfileSettings();
    }, [user]);

    const handleSaveUnitPreferences = async () => {
      setSaving(true);
      setMessage(null);

      try {
        setDefaultUnitSystem(unitSystem);

        if (user?.id) {
          await saveSettingToDatabase(user.id, SETTINGS_KEYS.UNIT_SYSTEM, unitSystem);
        }

        setMessage({ type: 'success', text: 'Unit preferences saved!' });
      } catch (e) {
        setMessage({ type: 'error', text: e.message });
      }

      setSaving(false);
    };

    const handleSaveGpsInterval = async () => {
      setSaving(true);
      setMessage(null);
      try {
        if (user?.id) {
          await saveSettingToDatabase(user.id, SETTINGS_KEYS.GPS_INTERVAL, gpsInterval);
        }
        setMessage({ type: 'success', text: 'GPS interval saved!' });
      } catch (e) {
        setMessage({ type: 'error', text: e.message });
      }
      setSaving(false);
    };

    const handleSaveWhatsApp = async () => {
      setSaving(true);
      setMessage(null);
    
      try {
        const localSuccess = saveWhatsAppConfig(whatsappConfig);
        
        let dbSuccess = false;
        if (user?.id) {
          dbSuccess = await saveSettingToDatabase(user.id, SETTINGS_KEYS.WHATSAPP, whatsappConfig);
        }
        
        if (localSuccess && (dbSuccess || !user?.id)) {
          setMessage({ type: 'success', text: 'WhatsApp settings saved!' });
        } else if (localSuccess && !dbSuccess) {
          setMessage({ type: 'warning', text: 'Settings saved locally but could not sync to database.' });
        } else {
          setMessage({ type: 'error', text: 'Failed to save settings' });
        }
      } catch (e) {
        setMessage({ type: 'error', text: e.message });
      }
    
      setSaving(false);
    };

  const handleSaveAI = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const localSuccess = saveAIConfig(aiConfig);
      
      let dbSuccess = false;
      if (user?.id) {
        dbSuccess = await saveSettingToDatabase(user.id, SETTINGS_KEYS.AI_DISPATCH, aiConfig);
      }
      
      if (localSuccess && (dbSuccess || !user?.id)) {
        setMessage({ type: 'success', text: 'AI settings saved!' });
      } else if (localSuccess && !dbSuccess) {
        setMessage({ type: 'warning', text: 'Settings saved locally but could not sync to database.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    
    setSaving(false);
  };

  const handleSaveGrok = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const localSuccess = saveGrokConfig(grokConfig);
      
      let dbSuccess = false;
      if (user?.id) {
        dbSuccess = await saveSettingToDatabase(user.id, SETTINGS_KEYS.GROK, grokConfig);
      }
      
      if (localSuccess && (dbSuccess || !user?.id)) {
        setMessage({ type: 'success', text: 'Grok-3 settings saved!' });
      } else if (localSuccess && !dbSuccess) {
        setMessage({ type: 'warning', text: 'Settings saved locally but could not sync to database.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    
    setSaving(false);
  };

  const handleTestGrok = async () => {
    if (!isGrokConfigured()) {
      setMessage({ type: 'error', text: 'Please save Grok-3 settings first' });
      return;
    }
    
    setMessage({ type: 'info', text: 'Testing Grok-3 connection...' });
    
    try {
      const config = getGrokConfig();
      const response = await fetch('https://api.x.ai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: config.model || 'grok-3',
          messages: [{ role: 'user', content: 'Hello' }],
          max_tokens: 10
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Grok-3 connection successful!' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: 'Grok-3 test failed: ' + (error.error?.message || 'Invalid API key') });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Grok-3 test failed: ' + e.message });
    }
  };

  const handleTestWhatsApp = async () => {
    if (!isWhatsAppConfigured()) {
      setMessage({ type: 'error', text: 'Please save WhatsApp settings first' });
      return;
    }
    
    setMessage({ type: 'info', text: 'Testing connection...' });
    
    // Simple validation test - actual message sending would require a valid number
    try {
      const config = getWhatsAppConfig();
      if (config.accountSid && config.authToken && config.fromNumber) {
        setMessage({ type: 'success', text: 'Configuration looks valid! Send a test message from the Notification Center.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Configuration test failed: ' + e.message });
    }
  };

  const handleTestAI = async () => {
    if (!isAIConfigured()) {
      setMessage({ type: 'error', text: 'Please save AI settings first' });
      return;
    }
    
    setMessage({ type: 'info', text: 'Testing AI connection...' });
    
    try {
      const config = getAIConfig();
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
          'Authorization': `Bearer ${config.apiKey}`
        }
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'AI connection successful!' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: 'AI test failed: ' + (error.error?.message || 'Invalid API key') });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'AI test failed: ' + e.message });
    }
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const providerSettings = EMAIL_PROVIDERS[emailConfig.provider];
      const configToSave = {
        ...emailConfig,
        imapHost: emailConfig.provider === 'CUSTOM' ? emailConfig.imapHost : providerSettings.imapHost,
        imapPort: emailConfig.provider === 'CUSTOM' ? emailConfig.imapPort : providerSettings.imapPort
      };
      const localSuccess = saveEmailConfig(configToSave);
      
      let dbSuccess = false;
      if (user?.id) {
        dbSuccess = await saveSettingToDatabase(user.id, SETTINGS_KEYS.EMAIL, configToSave);
      }
      
      if (localSuccess && (dbSuccess || !user?.id)) {
        setMessage({ type: 'success', text: 'Email settings saved!' });
      } else if (localSuccess && !dbSuccess) {
        setMessage({ type: 'warning', text: 'Settings saved locally but could not sync to database.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save email settings' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    
    setSaving(false);
  };

  const handleSaveGoogleMaps = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      let configToSave = { ...googleMapsConfig };
      
      // If home location is set and API key is available, geocode it to get coordinates
      // __BOARD_MAPS_KEY__ through the Maps JavaScript API (a website-restricted
      // browser key is refused by the REST geocoder); REST only as a fallback.
      const jsHome = googleMapsConfig.homeLocation ? await geocodeWithJsApi(googleMapsConfig.homeLocation, googleMapsConfig.region) : null;
      if (jsHome) {
        configToSave.homeLat = jsHome.lat;
        configToSave.homeLng = jsHome.lng;
        setGoogleMapsConfig(configToSave);
      } else if (googleMapsConfig.homeLocation && googleMapsConfig.googleMapsApiKey) {
        setMessage({ type: 'info', text: 'Geocoding home location...' });
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(googleMapsConfig.homeLocation)}${googleMapsConfig.region ? `&region=${googleMapsConfig.region}` : ''}&key=${googleMapsConfig.googleMapsApiKey}`
          );
          const data = await response.json();
          
          if (data.status === 'OK' && data.results.length > 0) {
            const location = data.results[0].geometry.location;
            configToSave.homeLat = location.lat;
            configToSave.homeLng = location.lng;
            // Update the state with the geocoded coordinates
            setGoogleMapsConfig(configToSave);
          }
        } catch (geocodeError) {
          console.warn('Could not geocode home location:', geocodeError);
          // Continue saving without coordinates
        }
      } else if (!googleMapsConfig.homeLocation) {
        // Clear coordinates if home location is cleared
        configToSave.homeLat = null;
        configToSave.homeLng = null;
      }
      
      // Save to localStorage for quick access
      const localSuccess = saveRoutingConfig(configToSave);
      
      // Save to database for persistence across sessions
      let dbSuccess = false;
      if (user?.id) {
        dbSuccess = await saveSettingToDatabase(user.id, SETTINGS_KEYS.ROUTING, configToSave);
      }
      
      if (localSuccess && (dbSuccess || !user?.id)) {
        setMessage({ type: 'success', text: 'Google Maps settings saved!' });
      } else if (localSuccess && !dbSuccess) {
        setMessage({ type: 'warning', text: 'Settings saved locally but could not sync to database. They may not persist after logout.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save Google Maps settings' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    
    setSaving(false);
  };

  // __BOARD_MAPS_KEY__ geocode through the Maps JavaScript API (loads the map
  // script with the shared key if needed). null when it is not available.
  const geocodeWithJsApi = async (address, region) => {
    try {
      const g = await ensureGoogleMaps();
      const geocoder = new g.Geocoder();
      const res = await new Promise((resolve, reject) => geocoder.geocode(
        { address, ...(region ? { region } : {}) },
        (r, status) => (status === 'OK' && r && r.length ? resolve(r) : reject(new Error(status)))));
      const loc = res[0].geometry.location;
      return { lat: loc.lat(), lng: loc.lng() };
    } catch (e) {
      return null;
    }
  };

  const handleSaveSharedMapsKey = async () => {
    const k = sharedMapsKey.trim();
    if (k && !looksLikeBrowserKey(k)) {
      setMessage({ type: 'error', text: 'That does not look like a Google API key (they start with "AIza"). Paste the browser key from Google Cloud > Credentials.' });
      return;
    }
    setSavingSharedKey(true);
    setMessage(null);
    const r = await saveSharedMapsKey(k);
    setSavingSharedKey(false);
    if (!r.ok) {
      setMessage({ type: 'error', text: 'Could not save the shared key: ' + r.error });
      return;
    }
    const changed = k !== sharedMapsKeySaved;
    setSharedMapsKeySaved(k);
    const mapLoaded = typeof window !== 'undefined' && window.google && window.google.maps;
    setMessage({ type: 'success', text: k
      ? ('Shared Google Maps key saved - every office computer uses it.' + (changed && mapLoaded ? ' Reload the page to start using it here.' : ''))
      : 'Shared key removed. Each login falls back to its own key (if any).' });
  };

  const handleTestGoogleMaps = async () => {
    const mapLoaded = typeof window !== 'undefined' && window.google && window.google.maps;
    if (mapLoaded && sharedMapsKey.trim() && sharedMapsKey.trim() !== sharedMapsKeySaved) {
      setMessage({ type: 'warning', text: 'Save the shared key first, then reload the page and test - the map already loaded with the old key.' });
      return;
    }
    if (!isGoogleMapsConfigured()) {
      setMessage({ type: 'error', text: mapsKeyProblemText('NO_KEY') });
      return;
    }

    setMessage({ type: 'info', text: 'Testing Google Maps connection...' });

    // __BOARD_MAPS_KEY__ test the way the board really uses the key: load the
    // Maps JavaScript API and geocode through it. (The old REST test refused a
    // correctly website-restricted browser key.)
    try {
      const g = await ensureGoogleMaps();
      const geocoder = new g.Geocoder();
      await new Promise((resolve, reject) => geocoder.geocode({ address: 'Toronto, ON, Canada' },
        (r, status) => (status === 'OK' ? resolve(r) : reject(new Error(status)))));
      setMessage({ type: 'success', text: 'Google Maps works: the map loaded and an address was found.' });
    } catch (e) {
      if (e && e.code) setMessage({ type: 'error', text: mapsKeyProblemText(e.code) });
      else if (String(e && e.message).includes('REQUEST_DENIED')) setMessage({ type: 'error', text: 'The map loads, but Google refused the address lookup: enable the Geocoding API for this key (and Directions + Places).' });
      else setMessage({ type: 'error', text: 'Google Maps test failed: ' + (e && e.message) });
    }
  };

  const handleParseMessage = async () => {
    if (!manualMessage.trim()) {
      setMessage({ type: 'error', text: 'Please enter a message to parse' });
      return;
    }
    
    setParsing(true);
    setParsedResult(null);
    setMessage({ type: 'info', text: 'Parsing message with Grok AI...' });
    
    try {
      const result = await parseMessageWithGrok(manualMessage);
      setParsedResult(result);
      
      if (result.confidence > 50) {
        setMessage({ type: 'success', text: `Parsed with ${result.confidence}% confidence using ${result.method}` });
      } else {
        setMessage({ type: 'warning', text: `Low confidence (${result.confidence}%) - missing fields: ${result.missingFields?.join(', ') || 'none'}` });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Parse failed: ' + e.message });
    }
    
    setParsing(false);
  };

  const handleCreateDraftLoad = async () => {
    if (!parsedResult) {
      setMessage({ type: 'error', text: 'No parsed result to create load from' });
      return;
    }
    
    try {
      const draftLoad = createDraftLoadFromMessage(parsedResult);
      
      const { error } = await supabase
        .from('loads')
        .insert({
          load_number: draftLoad.load_number,
          status: 'pending',
          commodity: draftLoad.commodity,
          weight_kg: draftLoad.weight_kg,
          length_cm: draftLoad.length_m ? draftLoad.length_m * 100 : null,
          width_cm: draftLoad.width_m ? draftLoad.width_m * 100 : null,
          height_cm: draftLoad.height_m ? draftLoad.height_m * 100 : null,
          notes: `AI Parsed (${draftLoad.ai_confidence}% confidence)\n${draftLoad.notes || ''}\nPickup: ${draftLoad.pickup_address || 'TBD'}\nDelivery: ${draftLoad.delivery_address || 'TBD'}`
        });
      
      if (error) throw error;
      
      setMessage({ type: 'success', text: 'Draft load created! Check the Kanban board.' });
      setManualMessage('');
      setParsedResult(null);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to create load: ' + e.message });
    }
  };

  const getWebhookUrl = () => {
    return `${window.location.origin}/api/webhook/whatsapp`;
  };

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const contentStyle = {
    backgroundColor: 'var(--surface-1)',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '2rem'
  };

  const tabStyle = (isActive) => ({
    padding: '0.75rem 1.5rem',
    backgroundColor: isActive ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
    color: 'var(--text-primary)',
    border: 'none',
    borderRadius: '5px 5px 0 0',
    cursor: 'pointer',
    fontWeight: isActive ? 'bold' : 'normal',
    marginRight: '0.5rem'
  });

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--border-default)',
    borderRadius: '5px',
    color: 'var(--text-primary)',
    fontSize: '1rem',
    marginBottom: '1rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: 'var(--accent-primary)',
    fontWeight: 'bold'
  };

  const buttonStyle = {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--btn-primary-bg)',
    color: 'var(--text-primary)',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    marginRight: '0.5rem'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div className="modal-content" style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>Settings</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Status Message */}
        {message && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '5px',
            marginBottom: '1rem',
            backgroundColor: message.type === 'success' ? 'var(--color-success)' : 
                           message.type === 'error' ? 'var(--color-danger)' : 'var(--accent-blue)'
          }}>
            {message.text}
          </div>
        )}

                {/* Tabs */}
                <div className="modal-tabs" style={{ marginBottom: '1.5rem', borderBottom: '2px solid var(--border-default)', display: 'flex', flexWrap: 'wrap' }}>
                  <button style={tabStyle(activeTab === 'preferences')} onClick={() => setActiveTab('preferences')}>
                    Preferences
                  </button>
                  <button style={tabStyle(activeTab === 'integrations')} onClick={() => setActiveTab('integrations')}>
                    Integrations
                  </button>
                  <button style={tabStyle(activeTab === 'whatsapp')} onClick={() => setActiveTab('whatsapp')}>
                    WhatsApp (Twilio)
                  </button>
                  <button style={tabStyle(activeTab === 'grok')} onClick={() => setActiveTab('grok')}>
                    Grok-3 (xAI)
                  </button>
                                  <button style={tabStyle(activeTab === 'ai')} onClick={() => setActiveTab('ai')}>
                                    AI Dispatch (OpenAI)
                                  </button>
                                  <button style={tabStyle(activeTab === 'googlemaps')} onClick={() => setActiveTab('googlemaps')}>
                                    Google Maps
                                  </button>
                                  <button style={tabStyle(activeTab === 'dwell')} onClick={() => setActiveTab('dwell')}>
                                    Dwell Times
                                  </button>
                                  <button style={tabStyle(activeTab === 'equipment')} onClick={() => setActiveTab('equipment')}>
                                    Equipment rules
                                  </button>
                                  <button style={tabStyle(activeTab === 'daygraph')} onClick={() => setActiveTab('daygraph')}>
                                    Day graph
                                  </button>
                                </div>

                {/* Preferences Tab */}
                {activeTab === 'preferences' && (
                  <div>
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
                      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        Configure your display preferences for measurements and units.
                      </p>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={labelStyle}>Unit System</label>
                      <p style={{ margin: '0 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        Choose how dimensions and weights are displayed throughout the app.
                      </p>
              
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                          onClick={() => setUnitSystem(UNIT_SYSTEMS.IMPERIAL)}
                          style={{
                            flex: 1,
                            padding: '1.5rem',
                            backgroundColor: unitSystem === UNIT_SYSTEMS.IMPERIAL ? 'var(--accent-primary)' : 'var(--surface-2)',
                            border: unitSystem === UNIT_SYSTEMS.IMPERIAL ? '2px solid var(--accent-primary)' : '2px solid var(--border-default)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                            Imperial
                          </div>
                          <div style={{ color: unitSystem === UNIT_SYSTEMS.IMPERIAL ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: '0.9rem' }}>
                            Feet (ft) & Pounds (lbs)
                          </div>
                        </button>
                
                        <button
                          onClick={() => setUnitSystem(UNIT_SYSTEMS.METRIC)}
                          style={{
                            flex: 1,
                            padding: '1.5rem',
                            backgroundColor: unitSystem === UNIT_SYSTEMS.METRIC ? 'var(--accent-primary)' : 'var(--surface-2)',
                            border: unitSystem === UNIT_SYSTEMS.METRIC ? '2px solid var(--accent-primary)' : '2px solid var(--border-default)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                            Metric
                          </div>
                          <div style={{ color: unitSystem === UNIT_SYSTEMS.METRIC ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: '0.9rem' }}>
                            Centimeters (cm) & Kilograms (kg)
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Live preview */}
                    <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--accent-primary)', fontSize: '0.95rem' }}>Preview</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.9rem' }}>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Weight (5,000 kg example)</div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{formatWeightBoth(5000, unitSystem)}</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Length (400 cm example)</div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{formatLengthBoth(400, unitSystem)}</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Width (250 cm example)</div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{formatLengthBoth(250, unitSystem)}</div>
                        </div>
                        <div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Height (180 cm example)</div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{formatLengthBoth(180, unitSystem)}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-warning)' }}>How It Works</h4>
                      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        All measurements are stored in metric (cm/kg) in the database. Your preference controls how values 
                        are displayed in the app and exported to CSV. Both units are always shown in brackets for reference.
                      </p>
                    </div>

                    <div style={{ marginTop: '1.5rem' }}>
                      <button onClick={handleSaveUnitPreferences} disabled={saving} style={buttonStyle}>
                        {saving ? 'Saving...' : 'Save Preferences'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Integrations Tab */}
                {activeTab === 'integrations' && (
                  <div>
                    {/* Status Overview */}
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-primary)' }}>Integration Status</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Email</div>
                          <div style={{ color: isEmailConfigured() ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 'bold' }}>
                            {isEmailConfigured() ? '● Connected' : '○ Not configured'}
                          </div>
                          {lastEmailSync && (
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                              Last sync: {new Date(lastEmailSync).toLocaleString()}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>WhatsApp</div>
                          <div style={{ color: isWhatsAppConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontWeight: 'bold' }}>
                            {isWhatsAppConfigured() ? '● API Ready' : '○ Webhook Ready'}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            {whatsappMessageCount} messages received
                          </div>
                        </div>
                        <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Grok AI</div>
                          <div style={{ color: isGrokConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontWeight: 'bold' }}>
                            {isGrokConfigured() ? '● Configured' : '○ Rule-based'}
                          </div>
                        </div>
                        <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>SMS (Android)</div>
                          <div style={{ color: 'var(--color-warning)', fontWeight: 'bold' }}>
                            ○ App Required
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            Install Dispatcher Companion
                          </div>
                        </div>
                        <div style={{ padding: '0.75rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Google Maps</div>
                          <div style={{ color: isGoogleMapsConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontWeight: 'bold' }}>
                            {isGoogleMapsConfigured() ? '● Configured' : '○ Simulated'}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            {isGoogleMapsConfigured() ? 'Real routing enabled' : 'Using simulated routes'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Configuration */}
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-primary)' }}>Email Monitoring</h4>
                      <p style={{ margin: '0 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        Connect your dispatch email to automatically receive and parse load requests.
                      </p>
                      
                      <div>
                        <label style={labelStyle}>Email Provider</label>
                        <select
                          value={emailConfig.provider}
                          onChange={(e) => setEmailConfig({ ...emailConfig, provider: e.target.value })}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                        >
                          {Object.entries(EMAIL_PROVIDERS).map(([key, provider]) => (
                            <option key={key} value={key}>{provider.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={labelStyle}>Email Address</label>
                        <input
                          type="email"
                          value={emailConfig.email}
                          onChange={(e) => setEmailConfig({ ...emailConfig, email: e.target.value })}
                          placeholder="dispatch@yourcompany.com"
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>App Password</label>
                        <input
                          type="password"
                          value={emailConfig.password}
                          onChange={(e) => setEmailConfig({ ...emailConfig, password: e.target.value })}
                          placeholder="Your app-specific password"
                          style={inputStyle}
                        />
                        {EMAIL_PROVIDERS[emailConfig.provider]?.requiresAppPassword && (
                          <p style={{ margin: '-0.5rem 0 1rem 0', color: 'var(--color-warning)', fontSize: '0.8rem' }}>
                            Generate an app password at:{' '}
                            <a href={EMAIL_PROVIDERS[emailConfig.provider].appPasswordUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>
                              {EMAIL_PROVIDERS[emailConfig.provider].appPasswordUrl}
                            </a>
                          </p>
                        )}
                      </div>

                      {emailConfig.provider === 'CUSTOM' && (
                        <>
                          <div>
                            <label style={labelStyle}>IMAP Host</label>
                            <input
                              type="text"
                              value={emailConfig.imapHost}
                              onChange={(e) => setEmailConfig({ ...emailConfig, imapHost: e.target.value })}
                              placeholder="imap.yourdomain.com"
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>IMAP Port</label>
                            <input
                              type="number"
                              value={emailConfig.imapPort}
                              onChange={(e) => setEmailConfig({ ...emailConfig, imapPort: parseInt(e.target.value) })}
                              placeholder="993"
                              style={inputStyle}
                            />
                          </div>
                        </>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                        <input
                          type="checkbox"
                          id="emailEnabled"
                          checked={emailConfig.enabled}
                          onChange={(e) => setEmailConfig({ ...emailConfig, enabled: e.target.checked })}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <label htmlFor="emailEnabled" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                          Enable email monitoring
                        </label>
                      </div>

                      <button onClick={handleSaveEmail} disabled={saving} style={buttonStyle}>
                        {saving ? 'Saving...' : 'Save Email Settings'}
                      </button>
                    </div>

                    {/* WhatsApp Webhook Info */}
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-primary)' }}>WhatsApp Business API</h4>
                      <p style={{ margin: '0 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        To receive WhatsApp messages automatically, you need WhatsApp Business API (via Meta or Twilio).
                        Configure your webhook URL in your provider's dashboard:
                      </p>
                      
                      <div style={{ padding: '0.75rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px', marginBottom: '1rem' }}>
                        <label style={{ ...labelStyle, marginBottom: '0.25rem' }}>Webhook URL</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type="text"
                            value={getWebhookUrl()}
                            readOnly
                            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(getWebhookUrl());
                              setMessage({ type: 'success', text: 'Webhook URL copied!' });
                            }}
                            style={{ ...buttonStyle, backgroundColor: 'var(--accent-blue)' }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>

                      <div style={{ padding: '0.75rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
                        <p style={{ margin: 0, color: 'var(--color-warning)', fontSize: '0.85rem' }}>
                          <strong>Note:</strong> The free WhatsApp Business App does not provide API access. 
                          You need to apply for WhatsApp Business API through Meta Cloud API or a provider like Twilio.
                        </p>
                      </div>
                    </div>

                    {/* Manual Message Input */}
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-primary)' }}>Manual Message Parser</h4>
                      <p style={{ margin: '0 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        Paste a WhatsApp or email message here to have Grok AI extract load details.
                      </p>
                      
                      <div>
                        <label style={labelStyle}>Message Content</label>
                        <textarea
                          value={manualMessage}
                          onChange={(e) => setManualMessage(e.target.value)}
                          placeholder="Paste customer message here...&#10;&#10;Example: Need pickup at 123 Main St tomorrow at 8am. Delivering 5000 lbs of gravel to 456 Oak Ave."
                          style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                        <button onClick={handleParseMessage} disabled={parsing} style={buttonStyle}>
                          {parsing ? 'Parsing...' : 'Parse with Grok AI'}
                        </button>
                        {parsedResult && (
                          <button onClick={handleCreateDraftLoad} style={{ ...buttonStyle, backgroundColor: 'var(--color-success)' }}>
                            Create Draft Load
                          </button>
                        )}
                      </div>

                      {parsedResult && (
                        <div style={{ padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
                          <h5 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent-primary)' }}>Parsed Result ({parsedResult.method})</h5>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                            <div><span style={{ color: 'var(--text-muted)' }}>Pickup:</span> <span style={{ color: 'var(--text-primary)' }}>{parsedResult.pickupAddress || 'Not found'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Delivery:</span> <span style={{ color: 'var(--text-primary)' }}>{parsedResult.deliveryAddress || 'Not found'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Commodity:</span> <span style={{ color: 'var(--text-primary)' }}>{parsedResult.commodity || 'Not found'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Weight:</span> <span style={{ color: 'var(--text-primary)' }}>{parsedResult.weightKg ? `${parsedResult.weightKg} kg` : 'Not found'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Urgency:</span> <span style={{ color: 'var(--text-primary)' }}>{parsedResult.urgency || 'normal'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Confidence:</span> <span style={{ color: parsedResult.confidence > 50 ? 'var(--color-success)' : 'var(--color-danger)' }}>{parsedResult.confidence}%</span></div>
                          </div>
                          {parsedResult.missingFields?.length > 0 && (
                            <div style={{ marginTop: '0.5rem', color: 'var(--color-warning)', fontSize: '0.8rem' }}>
                              Missing: {parsedResult.missingFields.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* WhatsApp Tab */}
                {activeTab === 'whatsapp' && (
          <div>
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Configure Twilio WhatsApp API to send driver notifications. 
                Get your credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>console.twilio.com</a>
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: isWhatsAppConfigured() ? 'var(--color-success)' : 'var(--color-danger)', fontSize: '0.9rem' }}>
                Status: {isWhatsAppConfigured() ? '● Configured' : '○ Not configured'}
              </p>
            </div>

            <div>
              <label style={labelStyle}>Account SID</label>
              <input
                type="text"
                value={whatsappConfig.accountSid}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, accountSid: e.target.value })}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Auth Token</label>
              <input
                type="password"
                value={whatsappConfig.authToken}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, authToken: e.target.value })}
                placeholder="Your Twilio Auth Token"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>WhatsApp From Number</label>
              <input
                type="text"
                value={whatsappConfig.fromNumber}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, fromNumber: e.target.value })}
                placeholder="+14155238886 (Twilio Sandbox) or your number"
                style={inputStyle}
              />
              <p style={{ margin: '-0.5rem 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                For testing, use Twilio's sandbox number. For production, use your approved WhatsApp Business number.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSaveWhatsApp} disabled={saving} style={buttonStyle}>
                {saving ? 'Saving...' : 'Save WhatsApp Settings'}
              </button>
              <button onClick={handleTestWhatsApp} style={{ ...buttonStyle, backgroundColor: 'var(--accent-blue)' }}>
                Test Connection
              </button>
            </div>
          </div>
        )}

        {/* Grok-3 Tab */}
        {activeTab === 'grok' && (
          <div>
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Configure xAI Grok-3 API for intelligent message parsing. Grok-3 extracts load details from incoming WhatsApp messages.
                Get your API key from <a href="https://x.ai" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>x.ai</a>
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: isGrokConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontSize: '0.9rem' }}>
                Status: {isGrokConfigured() ? '● Configured' : '○ Not configured (using rule-based parsing)'}
              </p>
            </div>

            <div>
              <label style={labelStyle}>xAI API Key</label>
              <input
                type="password"
                value={grokConfig.apiKey}
                onChange={(e) => setGrokConfig({ ...grokConfig, apiKey: e.target.value })}
                placeholder="xai-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Model</label>
              <select
                value={grokConfig.model}
                onChange={(e) => setGrokConfig({ ...grokConfig, model: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="grok-3">Grok-3 (Latest)</option>
                <option value="grok-2">Grok-2</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSaveGrok} disabled={saving} style={buttonStyle}>
                {saving ? 'Saving...' : 'Save Grok-3 Settings'}
              </button>
              <button onClick={handleTestGrok} style={{ ...buttonStyle, backgroundColor: 'var(--accent-blue)' }}>
                Test Connection
              </button>
            </div>

            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-warning)' }}>What Grok-3 Does</h4>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Grok-3 parses incoming WhatsApp messages to extract: pickup/delivery addresses, commodity type, 
                weight, dimensions, time windows, and urgency. Without Grok-3, the system uses rule-based parsing 
                which is less accurate but still functional.
              </p>
            </div>
          </div>
        )}

        {/* AI Tab */}
        {activeTab === 'ai' && (
          <div>
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Configure OpenAI API for intelligent dispatch suggestions. 
                Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>platform.openai.com</a>
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: isAIConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontSize: '0.9rem' }}>
                Status: {isAIConfigured() ? '● Configured' : '○ Not configured (using rule-based suggestions)'}
              </p>
            </div>

            <div>
              <label style={labelStyle}>OpenAI API Key</label>
              <input
                type="password"
                value={aiConfig.apiKey}
                onChange={(e) => setAiConfig({ ...aiConfig, apiKey: e.target.value })}
                placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Model</label>
              <select
                value={aiConfig.model}
                onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Faster, Lower Cost)</option>
                <option value="gpt-4">GPT-4 (More Accurate, Higher Cost)</option>
                <option value="gpt-4-turbo">GPT-4 Turbo (Best Balance)</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSaveAI} disabled={saving} style={buttonStyle}>
                {saving ? 'Saving...' : 'Save AI Settings'}
              </button>
              <button onClick={handleTestAI} style={{ ...buttonStyle, backgroundColor: 'var(--accent-blue)' }}>
                Test Connection
              </button>
            </div>

            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-warning)' }}>Note: AI is Optional</h4>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Without AI configured, the system uses rule-based dispatch suggestions based on driver availability, 
                workload balance, and truck capacity. AI adds smarter recommendations but is not required.
              </p>
            </div>
          </div>
        )}

        {/* Google Maps Tab */}
        {activeTab === 'googlemaps' && (
          <div>
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Configure Google Maps API for route calculation, duration estimates, and geocoding.
                Get your API key from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>Google Cloud Console</a>
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: isGoogleMapsConfigured() ? 'var(--color-success)' : 'var(--color-warning)', fontSize: '0.9rem' }}>
                Status: {isGoogleMapsConfigured() ? '● Configured' : '○ Not configured (using simulated routes)'}
              </p>
            </div>

            {/* __BOARD_MAPS_KEY__ one key for every office computer */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px', border: '1px solid var(--accent-primary)' }}>
              <label style={labelStyle}>Shared Google Maps browser key (all office computers)</label>
              <input
                type="password"
                autoComplete="off"
                value={sharedMapsKey}
                onChange={(e) => setSharedMapsKey(e.target.value)}
                placeholder="AIza..."
                style={inputStyle}
              />
              <p style={{ margin: '-0.5rem 0 0.75rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                Use a <strong>browser</strong> key restricted to the website <code>https://hauling.hmgc.io/*</code>, with Maps JavaScript API, Directions API, Geocoding API and Places API enabled.
                Never paste the hub's server key here. Saved once, it works on every computer and login.
                {' '}Status: {sharedMapsKeySaved ? 'a shared key is set' : 'no shared key yet'}.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={handleSaveSharedMapsKey} disabled={savingSharedKey} style={buttonStyle}>
                  {savingSharedKey ? 'Saving...' : 'Save shared key'}
                </button>
                <button onClick={handleTestGoogleMaps} style={{ ...buttonStyle, backgroundColor: 'var(--btn-secondary-bg)' }}>
                  Test Google Maps
                </button>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Personal Google Maps key (this login only - used only when no shared key is set)</label>
              <input
                type="password"
                value={googleMapsConfig.googleMapsApiKey}
                onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, googleMapsApiKey: e.target.value })}
                placeholder="AIzaSy..."
                style={inputStyle}
              />
              <p style={{ margin: '-0.5rem 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                Optional. Leave empty when the shared key above is set.
              </p>
            </div>

            <div>
              <label style={labelStyle}>Region / Country</label>
              <select
                value={googleMapsConfig.region}
                onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, region: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Auto-detect</option>
                <option value="ca">Canada</option>
                <option value="us">United States</option>
                <option value="mx">Mexico</option>
                <option value="gb">United Kingdom</option>
                <option value="au">Australia</option>
                <option value="de">Germany</option>
                <option value="fr">France</option>
              </select>
              <p style={{ margin: '-0.5rem 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                Bias geocoding results toward this region for more accurate address lookups
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--surface-2)', borderRadius: '5px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--accent-primary)' }}>Default Map View</h4>
              <p style={{ margin: '0 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Set your home area to center the Route Builder map on your operating region by default.
              </p>
              
              <div>
                <label style={labelStyle}>Home Location</label>
                <input
                  type="text"
                  value={googleMapsConfig.homeLocation}
                  onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, homeLocation: e.target.value })}
                  placeholder="e.g., Winnipeg, MB, Canada"
                  style={inputStyle}
                />
                <p style={{ margin: '-0.5rem 0 1rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  Enter a city, town, or address to center the default map view
                </p>
              </div>

              <div>
                <label style={labelStyle}>View Radius: {googleMapsConfig.homeRadius} km</label>
                <input
                  type="range"
                  min="10"
                  max="500"
                  step="10"
                  value={googleMapsConfig.homeRadius}
                  onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, homeRadius: parseInt(e.target.value) })}
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  <span>10 km (local)</span>
                  <span>500 km (regional)</span>
                </div>
                <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  How wide an area to show around your home location
                </p>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Traffic Model</label>
              <select
                value={googleMapsConfig.trafficModel}
                onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, trafficModel: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="best_guess">Best Guess (Recommended)</option>
                <option value="pessimistic">Pessimistic (Longer estimates)</option>
                <option value="optimistic">Optimistic (Shorter estimates)</option>
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Day Start (daily plan baseline)</label>
              <input
                type="time"
                value={googleMapsConfig.dayStartTime || '07:00'}
                onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, dayStartTime: e.target.value || '07:00' })}
                style={{ ...inputStyle, maxWidth: '160px' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="startFromYard"
                  checked={googleMapsConfig.startFromYard !== false}
                  onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, startFromYard: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                <label htmlFor="startFromYard" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                  Plan starts at the Home Location (stop 1 ETA includes the drive from the yard)
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Routing Options</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="avoidTolls"
                    checked={googleMapsConfig.avoidTolls}
                    onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, avoidTolls: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <label htmlFor="avoidTolls" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                    Avoid toll roads
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="avoidHighways"
                    checked={googleMapsConfig.avoidHighways}
                    onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, avoidHighways: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <label htmlFor="avoidHighways" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                    Avoid highways
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="optimizeWaypoints"
                    checked={googleMapsConfig.optimizeWaypoints}
                    onChange={(e) => setGoogleMapsConfig({ ...googleMapsConfig, optimizeWaypoints: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <label htmlFor="optimizeWaypoints" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                    Optimize waypoint order for shortest route
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSaveGoogleMaps} disabled={saving} style={buttonStyle}>
                {saving ? 'Saving...' : 'Save Google Maps Settings'}
              </button>
              <button onClick={handleTestGoogleMaps} style={{ ...buttonStyle, backgroundColor: 'var(--accent-blue)' }}>
                Test Connection
              </button>
            </div>

            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-primary)', borderRadius: '5px' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-warning)' }}>API Key Security</h4>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                For security, restrict your API key in Google Cloud Console by HTTP referrer to only allow requests from your domain.
                Without Google Maps configured, the system uses simulated routes based on straight-line distances.
              </p>
            </div>
          </div>
        )}

        {/* Dwell Times Tab */}
        {activeTab === 'dwell' && (
          <DwellTimeSettings />
        )}

        {/* __EQUIP_COMPAT__ Equipment rules Tab */}
        {activeTab === 'equipment' && (
          <EquipmentRulesSettings />
        )}

        {/* __DAY_GRAPH__ the Day graph's columns (kept on the hub; the Dispatch app reads them too) */}
        {activeTab === 'daygraph' && (
          <DayGraphSettings />
        )}
      </div>
    </div>
  );
}

export default SettingsModal;
